/* agentSpecActions.ts */

/* Store */
import { AppDispatch, RootState } from "src/redux/store";

/* Actions */
import {
  applyAgentSpec,
  restoreInsightsFilter,
} from "src/redux/features/insights-filter/insightsFilterSlice";
import { setStartPoint } from "src/redux/features/insights-options/survival-analysis/survivalAnalysisOptionsSlice";
import { setMaxDepth } from "src/redux/features/insights-options/treatment-patterns/sankey-diagram/sankeyDiagramOptionsSlice";
import {
  clearUndoSnapshot,
  setUndoSnapshot,
} from "src/redux/features/agent-chat/agentChatSlice";

/* Interfaces */
import {
  AgentAnalysis,
  AgentSpec,
} from "src/shared/interfaces/api/AgentSpec";

/**
 * Apply a spec to the controls, taking an undo point first.
 *
 * Most of a spec lands in the insights filter with a single action.
 * The start point and the treatment-pattern depth live in their own
 * option slices, so they are dispatched separately — React batches
 * these together into one render, and the pages' fetch hook
 * debounces, so the applied spec still causes a single request.
 *
 * Navigation is left to the caller, which has the router — but the route it
 * is about to leave is recorded here, so undoing can return to it.
 */
export const applySpecToControls =
  (spec: AgentSpec, pathname: string) =>
  (dispatch: AppDispatch, getState: () => RootState): void => {
    const state = getState();

    dispatch(
      setUndoSnapshot({
        insightsFilter: state.insightsFilter,
        startPoint:
          state.survivalAnalysisOptions.startPoint.value,
        maxDepth: state.sankeyDiagramOptions.maxDepth,
        pathname,
      })
    );

    dispatch(
      applyAgentSpec({
        spec,
        allowedCareSites:
          state.auth.allowedCareSites ?? null,
      })
    );

    /**
     * Checked against the loaded start points for the same reason the
     * filter values are: an empty list means they have not loaded yet,
     * so there is nothing to judge against.
     */
    const startPoints: string[] =
      state.survivalAnalysisOptions.startPoint.items ?? [];

    if (
      spec.analysis === AgentAnalysis.SURVIVAL_ANALYSIS &&
      spec.start_point != null &&
      (!startPoints.length ||
        startPoints.includes(spec.start_point))
    ) {
      dispatch(setStartPoint(spec.start_point));
    }

    /* A depth of zero or below cannot be rendered or queried, so it is
       refused here rather than passed on as configuration. */
    if (
      spec.analysis === AgentAnalysis.TREATMENT_PATTERNS &&
      Number.isInteger(spec.max_depth) &&
      (spec.max_depth as number) > 0
    ) {
      dispatch(setMaxDepth(spec.max_depth as number));
    }
  };

/**
 * Put the controls back exactly as they were before the last
 * applied spec. Does nothing when there is no undo point.
 *
 * Returns the route to navigate back to, or ``null`` when there was
 * nothing to undo or the user never left the page. Navigating is the
 * caller's job, for the same reason as in ``applySpecToControls``.
 */
export const undoAppliedSpec =
  () =>
  (
    dispatch: AppDispatch,
    getState: () => RootState
  ): string | null => {
    const { undoSnapshot } = getState().agentChat;
    if (!undoSnapshot) return null;

    dispatch(
      restoreInsightsFilter(undoSnapshot.insightsFilter)
    );
    dispatch(setStartPoint(undoSnapshot.startPoint));
    dispatch(setMaxDepth(undoSnapshot.maxDepth));
    dispatch(clearUndoSnapshot());

    return undoSnapshot.pathname;
  };
